"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, Badge, Section } from "@/src/components";
import { Combobox, type ComboboxOption, type ComboboxGroup } from "@/src/components/input/combobox";
import { InstallCommand } from "@/components/docs/install-command";

const frameworks: ComboboxOption[] = [
  { value: "next", label: "Next.js" },
  { value: "sveltekit", label: "SvelteKit" },
  { value: "nuxt", label: "Nuxt.js" },
  { value: "remix", label: "Remix" },
  { value: "astro", label: "Astro" },
];

const groupedOptions: ComboboxGroup[] = [
  {
    label: "Frontend",
    options: [
      { value: "react", label: "React" },
      { value: "vue", label: "Vue" },
      { value: "svelte", label: "Svelte" },
    ],
  },
  {
    label: "Backend",
    options: [
      { value: "node", label: "Node.js" },
      { value: "python", label: "Python" },
      { value: "go", label: "Go" },
    ],
  },
];

const countries: ComboboxOption[] = [
  { value: "us", label: "United States" },
  { value: "ca", label: "Canada" },
  { value: "uk", label: "United Kingdom" },
  { value: "de", label: "Germany" },
  { value: "fr", label: "France" },
  { value: "es", label: "Spain" },
  { value: "it", label: "Italy" },
  { value: "jp", label: "Japan" },
  { value: "au", label: "Australia" },
];

export default function DocsComponentsCombobox() {
  const [value, setValue] = React.useState("");
  const [groupedValue, setGroupedValue] = React.useState("");
  const [countryValue, setCountryValue] = React.useState("");
  const [asyncValue, setAsyncValue] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [asyncOptions, setAsyncOptions] = React.useState<ComboboxOption[]>([]);

  const handleAsyncSearch = React.useCallback((query: string) => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const filtered = countries.filter((c) =>
        c.label.toLowerCase().includes(query.toLowerCase())
      );
      setAsyncOptions(filtered);
      setLoading(false);
    }, 500);
  }, []);

  return (
    <>
      {/* Page Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Combobox
            </Text>
            <Badge variant="secondary">Input</Badge>
          </div>
          <Text as="p" className="text-lg text-muted-foreground max-w-2xl">
            A searchable dropdown select component. Combines the functionality of a
            select with a search input for filtering options.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/combobox" />
        </SectionCard>
      </Section>

      {/* Basic Usage */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Basic Combobox</CardTitle>
              <CardDescription>Simple searchable select</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="flex flex-wrap items-center gap-4">
            <Combobox
              options={frameworks}
              value={value}
              onValueChange={setValue}
              placeholder="Select a framework..."
              searchPlaceholder="Search frameworks..."
              className="w-[200px]"
            />
            <Text as="p" className="text-sm text-muted-foreground">
              Selected: {value || "None"}
            </Text>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code className="block">{`const options = [`}</code>
            <code className="block">{`  { value: "next", label: "Next.js" },`}</code>
            <code className="block">{`  { value: "remix", label: "Remix" },`}</code>
            <code className="block">{`];`}</code>
            <code className="block mt-2">{`<Combobox`}</code>
            <code className="block">{`  options={options}`}</code>
            <code className="block">{`  value={value}`}</code>
            <code className="block">{`  onValueChange={setValue}`}</code>
            <code className="block">{`  placeholder="Select a framework..."`}</code>
            <code className="block">{`/>`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Grouped Options */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Grouped Options</CardTitle>
              <CardDescription>Options organized into groups</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="flex flex-wrap items-center gap-4">
            <Combobox
              options={groupedOptions}
              value={groupedValue}
              onValueChange={setGroupedValue}
              placeholder="Select a technology..."
              searchPlaceholder="Search technologies..."
              className="w-[200px]"
            />
            <Text as="p" className="text-sm text-muted-foreground">
              Selected: {groupedValue || "None"}
            </Text>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code className="block">{`const groupedOptions = [`}</code>
            <code className="block">{`  {`}</code>
            <code className="block">{`    label: "Frontend",`}</code>
            <code className="block">{`    options: [`}</code>
            <code className="block">{`      { value: "react", label: "React" },`}</code>
            <code className="block">{`    ],`}</code>
            <code className="block">{`  },`}</code>
            <code className="block">{`  {`}</code>
            <code className="block">{`    label: "Backend",`}</code>
            <code className="block">{`    options: [...],`}</code>
            <code className="block">{`  },`}</code>
            <code className="block">{`];`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Async Search */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Async Search</CardTitle>
              <CardDescription>Server-side filtering with loading state</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="flex flex-wrap items-center gap-4">
            <Combobox
              options={asyncOptions}
              value={asyncValue}
              onValueChange={setAsyncValue}
              onSearch={handleAsyncSearch}
              loading={loading}
              placeholder="Search countries..."
              searchPlaceholder="Type to search..."
              className="w-[200px]"
            />
            <Text as="p" className="text-sm text-muted-foreground">
              Selected: {asyncValue || "None"}
            </Text>
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code className="block">{`const handleSearch = (query: string) => {`}</code>
            <code className="block">{`  setLoading(true);`}</code>
            <code className="block">{`  fetchOptions(query).then((data) => {`}</code>
            <code className="block">{`    setOptions(data);`}</code>
            <code className="block">{`    setLoading(false);`}</code>
            <code className="block">{`  });`}</code>
            <code className="block">{`};`}</code>
            <code className="block mt-2">{`<Combobox`}</code>
            <code className="block">{`  options={options}`}</code>
            <code className="block">{`  onSearch={handleSearch}`}</code>
            <code className="block">{`  loading={loading}`}</code>
            <code className="block">{`/>`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Many Options */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Long List</CardTitle>
              <CardDescription>Scrollable list with many options</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <Combobox
            options={countries}
            value={countryValue}
            onValueChange={setCountryValue}
            placeholder="Select a country..."
            searchPlaceholder="Search countries..."
            className="w-[200px]"
          />
        </SectionCard>
      </Section>

      {/* Import */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Import</CardTitle>
              <CardDescription>How to import the component</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code className="block">{`import { Combobox } from "@/registry/new-york/ui/combobox";`}</code>
            <code className="block">{`import type { ComboboxOption, ComboboxGroup } from "@/registry/new-york/ui/combobox";`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Props */}
      <Section>
        <SectionCard
          header={<CardTitle>Props</CardTitle>}
          contentClassName="p-6"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-semibold">Prop</th>
                <th className="text-left py-2 font-semibold">Type</th>
                <th className="text-left py-2 font-semibold">Default</th>
                <th className="text-left py-2 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">options</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">ComboboxOption[] | ComboboxGroup[]</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Array of options or grouped options</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">value</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Currently selected value</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">onValueChange</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">(value: string) =&gt; void</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Callback when value changes</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">placeholder</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 font-mono text-xs">&quot;Select...&quot;</td>
                <td className="py-2 text-muted-foreground">Placeholder text when no value</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">searchPlaceholder</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 font-mono text-xs">&quot;Search...&quot;</td>
                <td className="py-2 text-muted-foreground">Search input placeholder</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">onSearch</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">(query: string) =&gt; void</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Callback for async search</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">loading</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 font-mono text-xs">false</td>
                <td className="py-2 text-muted-foreground">Show loading state</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">disabled</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 font-mono text-xs">false</td>
                <td className="py-2 text-muted-foreground">Disable the combobox</td>
              </tr>
            </tbody>
          </table>
        </SectionCard>
      </Section>
    </>
  );
}
